import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "flex items-center my-2" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "h-px mx-2 bg-gray-100 dark:bg-gray-700 flex-grow" }, null, -1)
const _hoisted_3 = ["innerHTML"]

import { ref, watchEffect, computed } from 'vue';
import TokenInput from '@/components/inputs/TokenInput/TokenInput.vue';
import TradePairToggle from './TradePairToggle.vue';
import { bnum } from '@/lib/utils';
import useNumbers, { FNumFormats } from '@/composables/useNumbers';
import useTokens from '@/composables/useTokens';
import { UseTrading } from '@/composables/trade/useTrading';

/**
 * TYPES
 */
type Props = {
  tokenInAmount: string;
  tokenInAddress: string;
  tokenOutAmount: string;
  tokenOutAddress: string;
  exactIn: boolean;
  priceImpact?: number;
  effectivePriceMessage?: UseTrading['effectivePriceMessage'];
  tradeLoading?: boolean;
};

/**
 * PROPS & EMITS
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'TradePair',
  props: {
    tokenInAmount: null,
    tokenInAddress: null,
    tokenOutAmount: null,
    tokenOutAddress: null,
    exactIn: { type: Boolean },
    priceImpact: null,
    effectivePriceMessage: null,
    tradeLoading: { type: Boolean }
  },
  emits: ["update:tokenInAmount", "update:tokenInAddress", "update:tokenOutAmount", "update:tokenOutAddress", "update:exactIn", "amountChange"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:tokenInAmount', value: string): void;
  (e: 'update:tokenInAddress', value: string): void;
  (e: 'update:tokenOutAmount', value: string): void;
  (e: 'update:tokenOutAddress', value: string): void;
  (e: 'update:exactIn', value: boolean): void;
  (e: 'amountChange'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  tokenInAmount: string;
  tokenInAddress: string;
  tokenOutAmount: string;
  tokenOutAddress: string;
  exactIn: boolean;
  priceImpact?: number;
  effectivePriceMessage?: UseTrading['effectivePriceMessage'];
  tradeLoading?: boolean;
};





/**
 * COMPOSABLES
 */
const { fNum2 } = useNumbers();
const { getToken } = useTokens();

/**
 * STATE
 */
const _tokenInAmount = ref<string>('');
const _tokenInAddress = ref<string>('');
const _tokenOutAmount = ref<string>('');
const _tokenOutAddress = ref<string>('');

const isInRate = ref<boolean>(true);

/**
 * COMPUTED
 */
const missingToken = computed(
  () => !_tokenInAddress.value || !_tokenOutAddress.value
);

const missingAmount = computed(
  () => !_tokenInAmount.value || !_tokenOutAmount.value
);

const tokenIn = computed(() => getToken(_tokenInAddress.value));
const tokenOut = computed(() => getToken(_tokenOutAddress.value));

const rateLabel = computed(() => {
  if (missingToken.value || missingAmount.value) return '';

  if (props.effectivePriceMessage)
    return isInRate.value
      ? props.effectivePriceMessage.value.tokenIn
      : props.effectivePriceMessage.value.tokenOut;

  let rate, inSymbol, outSymbol;

  if (isInRate.value) {
    rate = bnum(_tokenOutAmount.value).div(_tokenInAmount.value).toString();
    inSymbol = tokenIn.value.symbol;
    outSymbol = tokenOut.value.symbol;
  } else {
    rate = bnum(_tokenInAmount.value).div(_tokenOutAmount.value).toString();
    inSymbol = tokenOut.value.symbol;
    outSymbol = tokenIn.value.symbol;
  }

  return `1 ${inSymbol} = ${fNum2(rate, FNumFormats.token)} ${outSymbol}`;
});

/**
 * METHODS
 */
function handleInAmountChange(value: string): void {
  emit('update:exactIn', true);
  emit('update:tokenInAmount', value);
  emit('amountChange');
}

function handleOutAmountChange(value: string): void {
  emit('update:exactIn', false);
  emit('update:tokenOutAmount', value);
  emit('amountChange');
}

function handleTokenSwitch(): void {
  emit('update:exactIn', !props.exactIn);
  emit('update:tokenInAmount', _tokenOutAmount.value);
  emit('update:tokenInAddress', _tokenOutAddress.value);
  emit('update:tokenOutAmount', _tokenInAmount.value);
  emit('update:tokenOutAddress', _tokenInAddress.value);
  emit('amountChange');
}

async function handleInputTokenChange(newTokenIn: string) {
  if (newTokenIn === _tokenOutAddress.value) {
    handleTokenSwitch();
    return;
  }
  emit('update:tokenInAddress', newTokenIn);
}

async function handleOutputTokenChange(newTokenOut: string) {
  if (newTokenOut === _tokenInAddress.value) {
    handleTokenSwitch();
    return;
  }
  emit('update:tokenOutAddress', newTokenOut);
}

/**
 * CALLBACKS
 */
watchEffect(() => {
  _tokenInAmount.value = props.tokenInAmount;
  _tokenInAddress.value = props.tokenInAddress;
  _tokenOutAmount.value = props.tokenOutAmount;
  _tokenOutAddress.value = props.tokenOutAddress;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(TokenInput, {
      amount: _tokenInAmount.value,
      address: _tokenInAddress.value,
      name: "tokenIn",
      "onUpdate:amount": handleInAmountChange,
      "onUpdate:address": handleInputTokenChange,
      disabled: __props.tradeLoading
    }, null, 8, ["amount", "address", "disabled"]),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(TradePairToggle, { onToggle: handleTokenSwitch }),
      _hoisted_2,
      (_unref(rateLabel))
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "flex items-center text-xs text-gray-500 cursor-pointer",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (isInRate.value = !isInRate.value)),
            innerHTML: _unref(rateLabel)
          }, null, 8, _hoisted_3))
        : _createCommentVNode("", true)
    ]),
    _createVNode(TokenInput, {
      amount: _tokenOutAmount.value,
      address: _tokenOutAddress.value,
      name: "tokenOut",
      priceImpact: __props.priceImpact,
      "onUpdate:amount": handleOutAmountChange,
      "onUpdate:address": handleOutputTokenChange,
      noRules: "",
      noMax: "",
      disabled: __props.tradeLoading
    }, null, 8, ["amount", "address", "priceImpact", "disabled"])
  ]))
}
}

})